import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useFollowingStores } from "@/hooks/useFollowingStores";
import { useCartStore } from "@/stores/cartStore";
import { useSidebar } from "@/contexts/SidebarContext";
import type { LayoutProps } from "@/types/layout";
import { Navbar } from "./navbar/Navbar";
import { Sidebar } from "./sidebar/Sidebar";
import { CartDrawer } from "@/components/cart/CartDrawer";
import AppStorePopUp from "../shared/AppStorePopUp";
import { Footer } from "./footer/Footer";
import ScrollToTop from "@/components/shared/ScrollToTop";

export function Layout({ children }: LayoutProps) {
  const [isAppPopupOpen, setIsAppPopupOpen] = useState(false); 

  const location = useLocation();
  const navigate = useNavigate();
  const { isLoading: isAuthLoading } = useAuth();
  const { stores: followingStores, isLoading: isStoresLoading } = useFollowingStores();
  

  const { isSidebarOpen, isLargeScreen, toggleSidebar, closeSidebar } = useSidebar();

  const authPages = ["/login", "/signup", "/forgot-password"];
  const isAuthPage = authPages.includes(location.pathname);

  // Pages where footer should be shown
const pagesWithFooter = ["/", "/home", "/shop", "/live", "/products"];
const shouldShowFooter = pagesWithFooter.some(page => {
  if (page === "/") {
    return location.pathname === "/";
  }
  return location.pathname === page || location.pathname.startsWith(page + "/");
});

  const getActiveRoute = (): "home" | "shop" | "live" | "wallet" | undefined => {
    if (location.pathname === "/" || location.pathname === "/home") {
      return "home";
    }
    if (
      location.pathname === "/shop" ||
      location.pathname.startsWith("/shop")
    ) {
      return "shop";
    }
    if (location.pathname === "/live") {
      return "live";
    }
    if (location.pathname === "/wallet") {
      return "wallet";
    }
    return undefined;
  };

  const { totalItems, openCart } = useCartStore();

  const handleCartClick = () => {
    openCart();
  };

  const handleDownloadAppClick = () => {
    setIsAppPopupOpen(true); 
  };

  const handleStoreClick = (storeId: string) => {
    navigate(`/profile/${storeId}`);

    if (!isLargeScreen) {
      closeSidebar();
    }
  };

  const handleNavigate = (route: string) => {
    if (route === "home") {
      navigate("/");
    } else if (route === "shop") {
      navigate("/shop");
    } else if (route === "live") {
      navigate("/live");
    }

    if (!isLargeScreen) {
      closeSidebar();
    }
  };

  if (isAuthLoading && !isAuthPage) {
    return (
      <div className="min-h-screen w-full bg-[#F4F4F4] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (isAuthPage) {
    return <div className="min-h-screen w-full bg-[#F4F4F4]">{children}</div>;
  }

  return (
    <div className="min-h-screen h-screen w-full overflow-hidden bg-[#F4F4F4] flex flex-col">
      <Navbar
        cartItemCount={totalItems}
        onCartClick={handleCartClick}
        onDownloadAppClick={handleDownloadAppClick}
        onToggleSidebar={toggleSidebar}
        onLogoClick={() => handleNavigate("home")}
      />
      <CartDrawer />
      
      <AppStorePopUp open={isAppPopupOpen} onOpenChange={setIsAppPopupOpen} />
      <ScrollToTop />

      <div className="flex flex-1 overflow-hidden relative">
        {isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={toggleSidebar}
            aria-hidden="true"
          />
        )}

        <Sidebar
          followingStores={followingStores}
          isLoading={isStoresLoading}
          onStoreClick={handleStoreClick}
          onNavigate={handleNavigate}
          activeRoute={getActiveRoute()}
          isOpen={isSidebarOpen}
          onToggle={toggleSidebar}
        />

        <main className="flex-1 overflow-y-auto overflow-x-hidden bg-[#F4F4F4] flex flex-col scroll-container">
          <div className="flex-1">
            {children}
          </div>
          {shouldShowFooter && <Footer />}
        </main>
      </div>
    </div>
  );
}